import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle2, Clock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { api } from "../lib/api";

/**
 * Live attendance widget. Avatar row with green ring = confirmed, dimmed = pending.
 * Polls every 30s while mounted so creators see confirmations in near-real-time.
 *
 * Props:
 *   eventKind: "match" | "tournament" | "league" | "lesson"
 *   eventId: string
 */
export default function AttendanceWidget({ eventKind, eventId }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/events/${eventKind}/${eventId}/attendance`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [eventKind, eventId]);

  useEffect(() => {
    if (!eventId) return undefined;
    load();
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      load();
    }, 30 * 1000);
    return () => clearInterval(t);
  }, [eventId, load]);

  if (!data) return null;

  const { roster, confirmed, registered, capacity } = data;
  const pct = registered > 0 ? Math.round((confirmed / registered) * 100) : 0;
  const ringColor = pct >= 75 ? "ring-[#00E676]" : pct >= 40 ? "ring-[#FFD60A]" : "ring-[#FF3B30]/60";

  return (
    <div className="pp-card p-4" data-testid="attendance-widget">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Live attendance</div>
          <div className="font-bold text-sm flex items-center gap-2 mt-0.5">
            <span className={`inline-flex items-center gap-1 ${pct >= 75 ? "text-[#00E676]" : pct >= 40 ? "text-[#FFD60A]" : "text-zinc-300"}`} data-testid="attendance-stat">
              <CheckCircle2 size={14} /> {confirmed}/{registered} confirmed
            </span>
            {capacity > registered && <span className="text-zinc-500 text-xs">· {capacity - registered} open spot{capacity - registered === 1 ? "" : "s"}</span>}
          </div>
        </div>
        <div className={`shrink-0 w-12 h-12 rounded-full ring-2 ${ringColor} flex items-center justify-center text-xs font-bold`} aria-label={`${pct}% confirmed`}>
          {pct}%
        </div>
      </div>

      {roster.length === 0 ? (
        <div className="text-zinc-500 text-xs">No players yet — invite some.</div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="attendance-roster">
          {roster.map((p) => (
            <div key={p.user_id} className="flex items-center gap-2 pp-card px-2 py-1" title={p.confirmed ? "Confirmed" : "Pending"} data-testid={`attendance-${p.confirmed ? "confirmed" : "pending"}-${p.user_id}`}>
              <Avatar className={`h-7 w-7 ring-2 ${p.confirmed ? "ring-[#00E676]" : "ring-white/10 grayscale"}`}>
                <AvatarImage src={p.avatar_url || ""} />
                <AvatarFallback className="bg-[#007AFF] text-white text-[10px]">{(p.name || "?").charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="text-xs">{p.name || "Player"}</span>
              {p.confirmed
                ? <CheckCircle2 size={12} className="text-[#00E676]" />
                : <Clock size={12} className="text-zinc-500" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
